import React, { useState, useEffect } from "react";
import { 
  ListIcon, 
  FilterIcon, 
  CalendarIcon,
  AlertTriangle,
  ChevronLeftIcon,
  ChevronRightIcon 
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import { fetchLogs } from "../services/logsService";
import { toast } from "react-toastify";

// Add this helper function at the top of your component
const formatDateTime = (dateTimeString) => {
  try {
    if (!dateTimeString) return { date: 'No date', time: 'No time' };
    
    const date = new Date(dateTimeString);
    
    if (isNaN(date.getTime())) {
      console.error('Invalid date input:', dateTimeString);
      return { date: 'Invalid date', time: 'Invalid time' };
    }

    const formattedDate = date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC'
    });

    const formattedTime = date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'UTC'
    });

    return { date: formattedDate, time: formattedTime };
  } catch (error) {
    console.error('Date formatting error:', error);
    return { date: 'Error', time: 'Error' };
  }
};

function LogsAnalytics() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterAction, setFilterAction] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const loadLogs = async () => {
      try {
        setLoading(true);
        const filters = {};
        if (filterAction) filters.action = filterAction;
        if (filterDate) filters.date = filterDate;

        const { logs: logsData, pagination: paginationData } = await fetchLogs(filters);
        setLogs(logsData);
        setPagination(paginationData);
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch logs:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadLogs();
  }, [filterAction, filterDate]);

  // Add pagination handler
  const handlePageChange = async (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    
    try {
      setLoading(true);
      const filters = {
        page: newPage,
        ...(filterAction && { action: filterAction }),
        ...(filterDate && { date: filterDate })
      };

      const { logs: logsData, pagination: paginationData } = await fetchLogs(filters);
      setLogs(logsData);
      setPagination(paginationData);
      setCurrentPage(newPage);
    } catch (error) {
      console.error("Failed to fetch logs:", error);
      toast.error("Failed to load page");
    } finally {
      setLoading(false);
    }
  };

  // Update the filter logic to use dateTime
  const filteredLogs = Array.isArray(logs) ? logs.filter(log => {
    const matchesAction = filterAction ? 
      log?.action?.toLowerCase().includes(filterAction.toLowerCase()) : 
      true;
      
    const matchesDate = filterDate ? 
      new Date(log.dateTime).toISOString().split('T')[0] === filterDate : 
      true;
      
    return matchesAction && matchesDate;
  }) : [];

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <div className="flex-grow ml-64 p-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
            <ListIcon className="mr-4 w-8 h-8 text-blue-600" />
            Logs & Analytics
          </h2>

          {/* Filters */}
          <div className="bg-white shadow-md rounded-lg p-4 mb-6 flex items-center space-x-4">
            <div className="flex items-center flex-grow">
              <FilterIcon className="mr-2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Filter by Action"
                value={filterAction}
                onChange={(e) => setFilterAction(e.target.value)}
                className="flex-grow px-3 py-2 border-b border-gray-200 focus:outline-none"
              />
            </div>
            <div className="flex items-center flex-grow">
              <CalendarIcon className="mr-2 w-5 h-5 text-gray-500" />
              <input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="flex-grow px-3 py-2 border-b border-gray-200 focus:outline-none"
              />
            </div>
          </div>

          {/* Logs Table */}
          <div className="bg-white shadow-xl rounded-lg overflow-hidden">
            {logs.length === 0 ? (
              <div className="text-center py-12">
                <ListIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-lg font-medium text-gray-900">No Logs Found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {filterAction || filterDate 
                    ? "No logs match your current filters."
                    : "No logs have been recorded yet."}
                </p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-blue-800 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left">Date</th>
                    <th className="px-6 py-4 text-left">Time</th>
                    <th className="px-6 py-4 text-left">Action</th>
                    <th className="px-6 py-4 text-left">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.length > 0 ? (
                    filteredLogs.map((log) => {
                      const { date, time } = formatDateTime(log.dateTime);
                      return (
                        <tr 
                          key={log.id} 
                          className="border-b border-gray-200 hover:bg-gray-50 transition"
                        >
                          <td className="px-6 py-4 text-gray-700">{date}</td>
                          <td className="px-6 py-4 text-gray-700">{time}</td>
                          <td className="px-6 py-4">
                            <span className={`
                              px-3 py-1 rounded-full text-xs font-semibold
                              ${
                                log.action === "Email Processed" 
                                  ? "bg-blue-100 text-blue-800"
                                  : log.action === "Response Sent"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-800"
                              }
                            `}>
                              {log.action}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-gray-600">{log.detail}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center py-6 text-gray-500">
                        No logs found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {pagination && pagination.totalPages > 0 && (
            <div className="mt-4 flex justify-between items-center px-6">
              <p className="text-sm text-gray-600">
                Showing {logs.length} of {pagination.totalLogs} logs
              </p>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-md ${
                    currentPage === 1
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  aria-label="Previous page"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>
                
                <div className="flex items-center space-x-1">
                  {[...Array(pagination.totalPages)].map((_, idx) => {
                    const pageNumber = idx + 1;
                    const isCurrentPage = pageNumber === currentPage;
                    const isWithinRange = Math.abs(pageNumber - currentPage) <= 2;
                    
                    if (!isWithinRange && pageNumber !== 1 && pageNumber !== pagination.totalPages) {
                      if (pageNumber === 2 || pageNumber === pagination.totalPages - 1) {
                        return <span key={idx} className="px-2">...</span>;
                      }
                      return null;
                    }

                    return (
                      <button
                        key={idx}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`px-3 py-1 rounded-md ${
                          isCurrentPage
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === pagination.totalPages}
                  className={`p-2 rounded-md ${
                    currentPage === pagination.totalPages
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  aria-label="Next page"
                >
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LogsAnalytics;
